import Core from '../Core/Core';
import getLogger, { ILogger } from '../Core/Logger';
import { FocusManager } from '../Managers/FocusManager';
import { getAdByAction, getAdByType } from './AdPlacements';
import { SDKfb } from './SDKfb';
import { ParamGroups } from '../DeltaTracking/Tracker';
import { AdTypes } from '../DeltaTracking/AdParamBuilder';
import { v4 as uuid } from 'uuid';
import { IAdStages } from '../Managers/Tracking/Tracking';
// import AdTracking, { IAdStages } from '../Managers/Tracking/AdTracking';
import AdInstance = FBInstant.AdInstance;

const sdkLogger: ILogger = getLogger('[SDK-ADS]:');

export interface ISDKads {
    showAd: (
        adType: string,
        adAction: string,
        callback: () => void,
        errorCallback: () => void,
        displayAd?: boolean
    ) => Promise<any>;
    getRewardedEnabled: () => boolean;
    showLoadedAd: () => Promise<void>;
}

interface IAdInfo {
    placement: string;
    uuAdId: string;
    adType: string;
    adAction: string;
    callback: () => void;
    errorCallback: () => void;
    instance: AdInstance;
}

export class SDKads implements ISDKads {
    public platform: SDKfb;
    private cachedAd: IAdInfo;

    constructor(platform: SDKfb) {
        this.platform = platform;
    }

    public getRewardedEnabled() {
        return this.platform.getRewardedEnabled();
    }

    public async showAd(
        adType: string,
        adAction: string,
        callback: () => void,
        errorCallback: () => void,
        displayAd: boolean = true
    ): Promise<any> {
        const uuAdId = uuid();
        const placement = getAdByAction(adAction, getAdByType(adType));
        const t: AdTypes = adType === 'rewarded' ? AdTypes.REWARDED : AdTypes.INTERSTITIAL;
        Core.adTrackingHelper(t, IAdStages.REQUESTED, placement, 0, uuAdId, adAction);
        try {
            const instance = await this.platform.getAd(placement, adType);
            this.cachedAd = {
                placement,
                uuAdId,
                adType,
                adAction,
                callback,
                errorCallback,
                instance,
            };
            if (displayAd) {
                await this.showLoadedAd();
            }
        } catch (err) {
            sdkLogger.debug('get ad error: ' + JSON.stringify(err));
            if (adType === 'rewarded') {
                Core.adTrackingHelper(
                    AdTypes.REWARDED,
                    IAdStages.FAILED,
                    placement,
                    0,
                    uuAdId,
                    adAction
                );
            }
            if (err === 'ADS_TOO_MANY_INSTANCES') {
                Core.tooManyAds();
            }
            errorCallback();
        }
    }

    public async showLoadedAd() {
        if (!this.cachedAd) {
            return Promise.resolve();
        }
        const {
            instance,
            adType,
            callback,
            errorCallback,
            uuAdId,
            placement,
            adAction,
        } = this.cachedAd;
        this.cachedAd = null;
        const savedDeltaData = Core.delta.trackCurPropGetters.get(ParamGroups.SAVED)();
        const sessionDeltaData = Core.delta.trackCurPropGetters.get(ParamGroups.SESSION)();
        const beforeAdLoaded = Date.now(); // this needs to move to request add
        Core.sdk.adPlaying = true;
        FocusManager.instance.unFocus();
        const afterAdLoaded = Date.now();
        try {
            await this.platform.showLoadedAd(instance);
            if (adType === 'rewarded') {
                const lastRewardedTime = Date.now();
                Core.delta.setSavedParams({
                    adsWatchedRewarded: savedDeltaData.adsWatchedRewarded + 1,
                    adsLastTimeShownRewarded: lastRewardedTime,
                    adsLastTimeShown: lastRewardedTime,
                });
                Core.delta.setSessionParams({
                    adsWatchedThisSessionRewarded:
                        sessionDeltaData.adsWatchedThisSessionRewarded + 1,
                });
            } else {
                const lastInterstiialTime = Date.now();
                Core.delta.setSavedParams({
                    adsWatchedInterstitial: savedDeltaData.adsWatchedInterstitial + 1,
                    adsLastTimeShown: lastInterstiialTime,
                });
                Core.delta.setSessionParams({
                    adsWatchedThisSessionInterstitial:
                        sessionDeltaData.adsWatchedThisSessionInterstitial + 1,
                });
            }

            const t: AdTypes = adType === 'rewarded' ? AdTypes.REWARDED : AdTypes.INTERSTITIAL;
            Core.adTrackingHelper(
                t,
                IAdStages.DELIVERED,
                placement,
                afterAdLoaded - beforeAdLoaded,
                uuAdId,
                adAction
            );

            callback();
        } catch (err) {
            sdkLogger.debug('show ad error: ' + err);
            if (adType === 'rewarded') {
                Core.adTrackingHelper(
                    AdTypes.REWARDED,
                    IAdStages.FAILED,
                    placement,
                    0,
                    uuAdId,
                    adAction
                );
            }
            if (err === 'ADS_TOO_MANY_INSTANCES') {
                Core.tooManyAds();
            }
            errorCallback();
        } finally {
            Core.sdk.adPlaying = false;
            FocusManager.instance.focus();
        }
    }
}
